'use client';
import { Menu, MenuButton, MenuItems } from '@headlessui/react';
import clsx from 'clsx';
import Link from 'next/link';
import { Fragment, useContext, useMemo } from 'react';

import { HeaderMenuItem } from '@/features/user-nav/ui/header-menu-item';
import { ChevronRightIcon } from '@/shared';
import { cn } from '@/shared/utils';

import { BreadcrumbsItem } from '../config';
import { BreadcrumbsContext } from '../providers';

type BreadcrumbsProps = {
	className?: string;
	size?: 'sm' | 'md';
};

export function Breadcrumbs({ className }: BreadcrumbsProps) {
	const { list } = useContext(BreadcrumbsContext);

	const rows = useMemo(() => {
		return list
			.filter((r) => r.title !== '')
			.reduce<BreadcrumbsItem[]>(
				(acc, val) => [...acc, val, { title: '>' }],
				[],
			)
			.slice(0, -1);
	}, [list]);

	return (
		<div
			className={cn(
				'flex flex-1 flex-wrap items-center text-xs md:text-sm/4',
				className,
			)}
			aria-label="breadcrumb"
		>
			{rows.map((val, key) => {
				if (val.children?.length) {
					return (
						<Menu key={key} as="div" className="relative cursor-pointer">
							<MenuButton as="div" className={'flex items-center gap-1'}>
								{({ open }) => (
									<>
										{val.title}
										<ChevronRightIcon
											className={clsx(
												{ 'rotate-270': open },
												{ 'rotate-90': !open },
												'size-5 transform transition-transform duration-200',
											)}
										/>
									</>
								)}
							</MenuButton>
							<MenuItems
								transition
								className="absolute left-0 z-10 mt-2 min-w-[300px] origin-top-right rounded-lg bg-white p-2 shadow-listbox ring-1 ring-inset ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in md:w-[400px]"
							>
								{val.children.map((item, key) => (
									<HeaderMenuItem
										key={key}
										href={item.href}
										name={item.title}
										hint={item.description}
									/>
								))}
							</MenuItems>
						</Menu>
					);
				}
				if (!val.href) {
					return (
						<span key={key} className="text-gray-2">
							{val.title === '>' ? (
								<ChevronRightIcon className="size-3 md:size-4" />
							) : (
								val.title
							)}
						</span>
					);
				}
				return (
					<Fragment key={key}>
						<Link
							key={key}
							href={val.href}
							className="text-gray-2 hover:underline"
						>
							{val.title}
						</Link>
					</Fragment>
				);
			})}
		</div>
	);
}
